import { BiColumnOverride, kBiCsvSheetName } from './upload';

// Mirror of backend `models/src/bi/answer.ts` (the taps euler sends).

/**
 * A tap in a BI widget, sent on the next `/ask` beside the message. The
 * server settles it without the model and writes the tap's words itself.
 */
export enum BiAnswerAction {
  Add = 'add',
  ConfirmUpload = 'confirm_upload',
  SelectSheets = 'select_sheets',
  UseExistingDataset = 'use_existing_dataset',
  SelectDataset = 'select_dataset',
  ApplyChanges = 'apply_changes',
  DiscardChanges = 'discard_changes',
  Undo = 'undo',
}

/** Mirror of backend `kBiNewDashboardName`: a new dashboard left unnamed. */
export const kBiNewDashboardName = '新看板';

export type BiAddTarget =
  | { dashboardId: string }
  /** A new dashboard; without a name it takes the default new name. */
  | { newDashboardName?: string };

export interface BiAddAnswer {
  action: BiAnswerAction.Add;
  draftIds: string[];
  /** Absent: the server picks, or asks with `bi_dashboard_choice`. */
  target?: BiAddTarget;
}

/** A sheet upload's column confirmation, with the user's corrections. */
export interface BiConfirmUploadAnswer {
  action: BiAnswerAction.ConfirmUpload;
  /** A sheet upload. */
  uploadId: string;
  generation: number;
  overrides: BiColumnOverride[];
  description?: string;
}

/** The sheets of a file upload to import, one dataset each. */
export interface BiSelectSheetsAnswer {
  action: BiAnswerAction.SelectSheets;
  /** A file upload. */
  uploadId: string;
  sheets: string[];
}

/** **Use existing**: a dataset made from the same file before. */
export interface BiUseExistingDatasetAnswer {
  action: BiAnswerAction.UseExistingDataset;
  uploadId: string;
  datasetId: string;
}

/** A dataset picked in `bi_dataset_choice`; it continues the agent turn. */
export interface BiSelectDatasetAnswer {
  action: BiAnswerAction.SelectDataset;
  /** An uploaded dataset id, or a SQL dataset's model key. */
  datasetId: string;
}

export interface BiApplyChangesAnswer {
  action: BiAnswerAction.ApplyChanges;
  changeSetId: string;
}

export interface BiDiscardChangesAnswer {
  action: BiAnswerAction.DiscardChanges;
  changeSetId: string;
}

export interface BiUndoAnswer {
  action: BiAnswerAction.Undo;
  dashboardId: string;
  /** The revision to undo; it must still be the current one. */
  revision: number;
}

export type BiAnswer =
  | BiAddAnswer
  | BiConfirmUploadAnswer
  | BiSelectSheetsAnswer
  | BiUseExistingDatasetAnswer
  | BiSelectDatasetAnswer
  | BiApplyChangesAnswer
  | BiDiscardChangesAnswer
  | BiUndoAnswer;

/**
 * The tap in words, as the server stores it (`kBiAnswerCopy`), so the
 * bubble on screen reads like the one history replays.
 */
export function biAnswerText(
  answer: BiAnswer,
  /**
   * What the tap names: the dashboard an add goes to, the sheet a
   * confirmation is for, the dataset a pick uses.
   */
  name?: string,
): string {
  switch (answer.action) {
    case BiAnswerAction.Add: {
      const target = answer.target;
      if (target && 'dashboardId' in target && name) return `加到「${name}」`;
      if (target && !('dashboardId' in target))
        return `加到新看板「${
          target.newDashboardName || kBiNewDashboardName
        }」`;
      return answer.draftIds.length > 1
        ? `把这 ${answer.draftIds.length} 张图表加到看板`
        : '把这张图表加到看板';
    }
    case BiAnswerAction.SelectSheets:
      return `导入工作表${answer.sheets.map(sheet => `「${sheet}」`).join('')}`;
    case BiAnswerAction.ConfirmUpload:
      return name && name !== kBiCsvSheetName
        ? `确认「${name}」的列`
        : '确认这些列';
    case BiAnswerAction.UseExistingDataset:
    case BiAnswerAction.SelectDataset:
      return name ? `用「${name}」这份数据` : '用这份数据';
    case BiAnswerAction.ApplyChanges:
      return '应用这些修改';
    case BiAnswerAction.DiscardChanges:
      return '放弃这些修改';
    case BiAnswerAction.Undo:
      return '撤销上一步修改';
  }
}
